import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
    TraderColumn,
    TraderColumnGroup,
} from '../components/traderColumns';
import {
    UNMEASURED_WIDTH,
    editPref,
    readPref,
    visibleColumns,
    writePref,
    type ColumnsChange,
    type TraderColumnsPref,
} from './traderColumnsPref';

export interface TraderColumnsView {
    columns: TraderColumn[];
    mode: TraderColumnsPref['mode'];
    hide: (key: string) => void;
    show: (key: string) => void;
    hideGroup: (group: TraderColumnGroup) => void;
    showGroup: (group: TraderColumnGroup) => void;
    showAll: () => void;
    fitToWidth: () => void;
}

// The visible trader columns for the measured width of the result box
// (0 before the first measurement).
export function useTraderColumns(width: number): TraderColumnsView {
    const [pref, setPref] = useState<TraderColumnsPref>(() => readPref());
    const effective = width > 0 ? width : UNMEASURED_WIDTH;

    const fresh = visibleColumns(pref, effective);
    const signature = fresh.map(c => c.key).join(',');
    // The rows are memoised: the array only changes when the set does, not
    // with every pixel of a resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const columns = useMemo(() => fresh, [signature]);

    // Stored after each change; the value read at mount is not written back.
    const stored = useRef(pref);
    useEffect(() => {
        if (pref === stored.current) return;
        stored.current = pref;
        writePref(pref);
    }, [pref]);

    const edit = useCallback(
        (change: ColumnsChange) =>
            setPref(previous => editPref(previous, effective, change)),
        [effective]
    );

    return {
        columns,
        mode: pref.mode,
        hide: key => edit({ kind: 'hide', key }),
        show: key => edit({ kind: 'show', key }),
        hideGroup: group => edit({ kind: 'hideGroup', group }),
        showGroup: group => edit({ kind: 'showGroup', group }),
        showAll: () => setPref({ mode: 'custom', hidden: [] }),
        fitToWidth: () => setPref({ mode: 'auto' }),
    };
}
